import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction } from '@lyearn/i18n';
import { CloudUpload, CopyIcon, EditOutline, EyeOutline } from '@lyearn/icons';
import { ActionMenuProps } from '@lyearn/molecules';
import { ContentEntityConfig } from '@/components/FilterManager/EntityFilter/entity/content';
import { UserEntityConfig } from '@/components/FilterManager/EntityFilter/entity/user';
import {
  getAlphabeticallySortItem,
  getDefaultSortValue,
  getLastEditedSortItem,
  getNewestSortItem,
  getOldestSortItem,
} from '@/components/FilterManager/SortMenu/utils';
import { FilterManager, SortAndFilterItem } from '@/components/FilterManager/types';
import {
  KnowledgeAssetContentType,
  KnowledgeAssetIndustryEnum,
  KnowledgeAssetSubTypeEnum,
  KnowledgeAssetSubTypeVariantEnum,
  ResourceTypeEnum,
  UserTypeEnum,
} from '@/types/schema';

import { convertEnumToReadableString } from '../../types';
import { PersonaMenuAction, PersonasActions } from '../types';

const KNOWLEDGE_ASSET_SORT_ITEMS = [
  getLastEditedSortItem(),
  getAlphabeticallySortItem(),
  getNewestSortItem(),
  getOldestSortItem(),
];

export const getItemWidth = (assetType: KnowledgeAssetSubTypeEnum) => {
  switch (assetType) {
    case KnowledgeAssetSubTypeEnum.Persona:
    case KnowledgeAssetSubTypeEnum.CustomerStory:
      return 238;
    default:
      return 324;
  }
};

export const DEFAULT_KNOWLEDGE_ASSET_SORT = getDefaultSortValue(KNOWLEDGE_ASSET_SORT_ITEMS);

export function getSortAndFilterItems({
  t,
  assetType,
  type,
}: {
  t: TFunction;
  assetType: KnowledgeAssetSubTypeEnum;
  type?: ResourceTypeEnum;
}): SortAndFilterItem[] {
  const industryFilter: SortAndFilterItem = {
    type: FilterManager.Filter,
    id: 'industries',
    defaultPlaceholder: t('KnowledgeAsset:Filters.Industry'),
    isEnabled: type !== ResourceTypeEnum.Path,
    groups: [
      {
        id: 'industries',
        options: Object.values(KnowledgeAssetIndustryEnum).map((industry) => ({
          value: industry,
          label: convertEnumToReadableString(industry),
        })),
      },
    ],
  };

  const personaTypeFilter: SortAndFilterItem = {
    type: FilterManager.Filter,
    id: 'knowledgeAssetSubTypeVariant',
    defaultPlaceholder: t('KnowledgeAsset:Filters.PersonaType'),
    isEnabled: true,
    groups: [
      {
        id: 'knowledgeAssetSubTypeVariant',
        options: Object.values(KnowledgeAssetSubTypeVariantEnum).map((personaType) => ({
          value: personaType,
          label: convertEnumToReadableString(personaType),
        })),
      },
    ],
  };

  const personaFilter: SortAndFilterItem = {
    type: FilterManager.Entity,
    isEnabled: true,
    id: 'childKnowledgeAssetIds',
    defaultPlaceholder: t('KnowledgeAsset:Filters.Persona'),
    entity: 'content',
    listConfig: ContentEntityConfig,
    queryParams: {
      types: [ResourceTypeEnum.KnowledgeAsset],
      dependentFilters: {
        knowledgeAssetSubTypes: [KnowledgeAssetSubTypeEnum.Persona],
      },
    },
  };

  const solutionFilter: SortAndFilterItem = {
    type: FilterManager.Entity,
    isEnabled: true,
    id: 'childKnowledgeAssetIds',
    defaultPlaceholder: t('KnowledgeAsset:Filters.Solution'),
    entity: 'content',
    listConfig: ContentEntityConfig,
    queryParams: {
      types: [ResourceTypeEnum.KnowledgeAsset],
      dependentFilters: {
        knowledgeAssetSubTypes: [KnowledgeAssetSubTypeEnum.Solution],
      },
    },
  };

  const filterItems: SortAndFilterItem[] = [
    {
      type: FilterManager.Sort,
      isEnabled: true,
      items: KNOWLEDGE_ASSET_SORT_ITEMS,
    },
  ];

  switch (assetType) {
    case KnowledgeAssetSubTypeEnum.Persona:
      filterItems.push(personaTypeFilter, industryFilter);
      break;
    case KnowledgeAssetSubTypeEnum.PersonaJourney:
      filterItems.push(personaFilter);
      break;
    case KnowledgeAssetSubTypeEnum.CustomerStory:
      filterItems.push(industryFilter);
      break;
    case KnowledgeAssetSubTypeEnum.Solution:
      filterItems.push(personaFilter);
      break;
    case KnowledgeAssetSubTypeEnum.SalesConversation:
      filterItems.push(personaFilter);
      break;
    case KnowledgeAssetSubTypeEnum.SolutionWin:
      filterItems.push(solutionFilter, personaFilter, industryFilter);
      break;
  }

  filterItems.push(
    {
      type: FilterManager.DateRange,
      id: 'createdAt',
      defaultPlaceholder: t('Common:Filter.CreatedOn'),
      isEnabled: true,
    },
    {
      type: FilterManager.Entity,
      isEnabled: true,
      id: 'createdByIds',
      defaultPlaceholder: t('Common:Filter.CreatedBy'),
      entity: 'user',
      listConfig: UserEntityConfig,
      queryParams: {
        userTypes: [UserTypeEnum.Staff],
      },
    },
  );

  return filterItems;
}

export function getMenuItems({ t }: { t: TFunction }) {
  return getEnabledItems([
    {
      id: PersonaMenuAction.View,
      label: t('Common:Actions.View'),
      EndIcon: EyeOutline,
      enabled: true,
    },
    {
      id: PersonaMenuAction.Edit,
      label: t('Common:Actions.Edit'),
      EndIcon: EditOutline,
      enabled: true,
    },
    {
      id: PersonaMenuAction.Duplicate,
      label: t('Common:Actions.Duplicate'),
      EndIcon: CopyIcon,
      enabled: true,
    },
  ]) as ActionMenuProps['items'];
}

export const getActionMenuItems = (t: TFunction) => {
  return [
    {
      id: PersonasActions.BulkImport,
      label: t('KnowledgeAsset:ImportPersonas'),
      EndIcon: CloudUpload,
    },
  ] as ActionMenuProps['items'];
};

export const getClassifiedChildKnowledgeAssets = (knowledgeAsset: KnowledgeAssetContentType) => {
  const personas: string[] = [];
  const personaJourneys: string[] = [];
  const solutions: string[] = [];
  const customerStories: string[] = [];
  const salesConversations: string[] = [];
  const solutionWins: string[] = [];

  knowledgeAsset?.childKnowledgeAssets?.forEach((asset) => {
    switch (asset.knowledgeAssetSubType) {
      case KnowledgeAssetSubTypeEnum.Persona:
        personas.push(asset.name);
        break;
      case KnowledgeAssetSubTypeEnum.PersonaJourney:
        personaJourneys.push(asset.name);
        break;
      case KnowledgeAssetSubTypeEnum.Solution:
        solutions.push(asset.name);
        break;
      case KnowledgeAssetSubTypeEnum.CustomerStory:
        customerStories.push(asset.name);
        break;
      case KnowledgeAssetSubTypeEnum.SalesConversation:
        salesConversations.push(asset.name);
        break;
      case KnowledgeAssetSubTypeEnum.SolutionWin:
        solutionWins.push(asset.name);
        break;
    }
  });

  return {
    personas,
    personaJourneys,
    solutions,
    customerStories,
    salesConversations,
    solutionWins,
  };
};
